// Format currency in Indian Rupees
export function formatCurrency(value, options = {}) {
    const { decimals = 0, compact = false } = options;

    if (value === null || value === undefined || isNaN(value)) return '₹0';

    if (compact) {
        if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
        if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
        if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    }

    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
}

// Format number with Indian locale
export function formatNumber(value, options = {}) {
    const { decimals = 0, compact = false } = options;

    if (value === null || value === undefined || isNaN(value)) return '0';

    if (compact) {
        if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
        if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
        if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    }

    return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
}

// Format percentage
export function formatPercentage(value, options = {}) {
    const { decimals = 1, showSign = false } = options;

    if (value === null || value === undefined || isNaN(value)) return '0%';

    const formatted = value.toFixed(decimals);
    const sign = showSign && value > 0 ? '+' : '';

    return `${sign}${formatted}%`;
}

// Format date
export function formatDate(dateString, format = 'short') {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const options = {
        short: { month: 'short', day: 'numeric' },
        medium: { month: 'short', day: 'numeric', year: 'numeric' },
        long: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
        full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
        iso: null, // Will return ISO format
    };

    if (format === 'iso') {
        return date.toISOString().split('T')[0];
    }

    return date.toLocaleDateString('en-IN', options[format] || options.short);
}

// Format time
export function formatTime(timeString, format = '12h') {
    if (!timeString) return '';

    const [hours, minutes] = timeString.split(':').map(Number);

    if (format === '24h') {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

// Format duration in minutes
export function formatDuration(minutes) {
    if (!minutes || isNaN(minutes)) return '0 min';

    if (minutes < 60) return `${Math.round(minutes)} min`;

    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);

    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
}

// Format volume (kg, g, L, ml)
export function formatVolume(value, unit = 'kg') {
    if (value === null || value === undefined || isNaN(value)) return '0';

    const unitMap = {
        kg: { threshold: 1000, convert: 'g', factor: 1000 },
        g: { threshold: 0.001, convert: 'kg', factor: 0.001 },
        L: { threshold: 1000, convert: 'ml', factor: 1000 },
        ml: { threshold: 0.001, convert: 'L', factor: 0.001 },
        pack: { threshold: null },
        pcs: { threshold: null },
    };

    return `${formatNumber(value)} ${unit}`;
}

// Format time slot ID: "09001030" → "9:00 AM - 10:30 AM"
export function formatTimeSlot(slotId) {
    if (!slotId) return '-';
    const raw = String(slotId).replace(/\D/g, '');
    if (raw.length !== 8) return slotId;
    const sh = parseInt(raw.slice(0, 2), 10);
    const sm = raw.slice(2, 4);
    const eh = parseInt(raw.slice(4, 6), 10);
    const em = raw.slice(6, 8);
    const fmt = (h, m) => {
        const period = h >= 12 ? 'PM' : 'AM';
        const hour = h % 12 || 12;
        return `${hour}:${m} ${period}`;
    };
    return `${fmt(sh, sm)} - ${fmt(eh, em)}`;
}

// Format order ID for display
export function formatOrderId(orderId) {
    if (!orderId) return '';
    return orderId.replace('ORD-', '#');
}

// Format phone number
export function formatPhone(phone) {
    if (!phone) return '';
    return phone.replace(/(\+91)\s?(\d{5})(\d{5})/, '$1 $2 $3');
}

// Truncate text
export function truncateText(text, maxLength = 30) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
}

// Get relative time
export function getRelativeTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
}

export default {
    formatCurrency,
    formatNumber,
    formatPercentage,
    formatDate,
    formatTime,
    formatTimeSlot,
    formatDuration,
    formatVolume,
    formatOrderId,
    formatPhone,
    truncateText,
    getRelativeTime,
};
